import { useEffect, useMemo, useState } from "react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import MapboxMapComponent from "./component/MapboxMapComponent";

import { useRegionView } from "services/useRegionView";

import config from "../../config";

import { fetchClusters } from "./data/fetchClusters";
import { fetchAccidents } from "./data/fetchAccidents";
import { fetchInterventionCamera } from "./data/fetchInterventionCamera";
import { fetchSuggestions } from "./data/fetchSuggestions";
import { fetchEvents } from "./data/fetchEvents";
import { fetchGovAccident } from "./data/fetchGovAccident";

import accidentIcon from "../../assets/images/Synchro/events-icons/incident.png";
import cameraIcon from "../../assets/images/Synchro/events-icons/camera-primary.png";
import roadworksIcon from "../../assets/images/Synchro/events-icons/roadworks.png";
import constructionIcon from "../../assets/images/Synchro/events-icons/road-block.png";
import maintenanceIcon from "../../assets/images/Synchro/events-icons/under-construction-sign.png";
import temporaryTrafficManagementMeasureIcon from "../../assets/images/Synchro/events-icons/temporary-traffic-management-measure.png";
import bluetooth from "../../assets/images/Synchro/events-icons/connection.png";
import vms from "../../assets/images/Synchro/events-icons/bilboard.png";
import tech  from "../../assets/images/Synchro/events-icons/ai.png";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FilterAlt } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useMaterialUIController } from "context";

const INTERVENTION_CATEGORIES = [
  {
    id: "icon-camera-layer-tech",
    icon: tech,
    name: "Τεχνολογική παρέμβαση",
    category_id: 3,
    useSourceIcon: true,
  },
  {
    id: "icon-camera-layer-project",
    icon: constructionIcon,
    name: "Έργο",
    category_id: 2,
  },
  {
    id: "icon-camera-layer-maintenance",
    icon: maintenanceIcon,
    name: "Συντήρηση",
    category_id: 1,
  },
  {
    id: "icon-camera-layer-policing",
    icon: cameraIcon,
    name: "Αστυνόμευση",
    category_id: 4,
  },
];

function MapPage() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const PERIOD_OPTIONS = [
    { value: "last1hour", label: "Τελευταία 1 ώρα" },
    { value: "last12hours", label: "Τελευταίες 12 ώρες" },
    { value: "last24hours", label: "Τελευταίες 24 ώρες" },
    { value: "lastWeek", label: "Τελευταία εβδομάδα" },
    { value: "lastMonth", label: "Τελευταίος μήνας" },
    { value: "custom", label: "Προσαρμοσμένες ημερομηνίες" },
  ];

  const [clusterData, setClusterData] = useState(null);
  const [suggestionData, setSuggestionData] = useState(null);
  const [accidentData, setAccidentData] = useState(null);
  const [interventionCameraData, setInterventionCameraData] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [govAccidentData, setGovAccidentData] = useState(null);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("last24hours");

  const [activeEvents, setActiveEvents] = useState(null);
  const [activeAccidents, setActiveAccidents] = useState(null);

  const [filteredEvents, setFilteredEvents] = useState(null);
  const [filterAccidents, setFilterAccidents] = useState(null);

  const { center, zoom } = useRegionView();

  // -------- Tablet UI detection --------
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [openDateModal, setOpenDateModal] = useState(false);
  const panelBackground = darkMode ? "#202940" : "#ffffff";
  const panelText = darkMode ? "#fff" : "#344767";
  const panelBorder = darkMode
    ? "1px solid rgba(255,255,255,0.12)"
    : "1px solid rgba(0,0,0,0.12)";
  const panelShadow = darkMode
    ? "0px 8px 24px rgba(0,0,0,0.3)"
    : "0px 8px 24px rgba(0,0,0,0.12)";

  function last24Layer(data) {
    if (!data || !Array.isArray(data.features)) return data;

    const now = dayjs();
    const last24 = now.subtract(24, "hour");

    const filtered = data.features.filter((f) => {
      const start = dayjs(f.properties?.valid_from, "DD/MM/YYYY HH:mm");
      return start.isSameOrAfter(last24) && start.isSameOrBefore(now);
    });
    console.log("Filtering accidents from last 24 hours:", filtered);
    return { ...data, features: filtered };
  }

  function statusActive(data) {
    if (!data || !Array.isArray(data.features)) return data;
    const filtered = data.features.filter(
      (f) => f?.properties?.check_status?.id === 1
    );
    return { ...data, features: filtered };
  }

  useEffect(() => {
    const loadData = () => {
      fetchClusters().then((data) => setClusterData(data));
      fetchSuggestions().then((data) => setSuggestionData(data));
      fetchAccidents().then((data) => {
        setActiveAccidents(last24Layer(data.data));
        setAccidentData(data.data);
      });
      fetchInterventionCamera().then((data) => setInterventionCameraData(data));
      fetchEvents().then((data) => {
        setActiveEvents(statusActive(data.data));
        setEventData(data.data);
      });
      fetchGovAccident().then((data) => setGovAccidentData(data));
    };

    loadData();
    const interval = setInterval(loadData, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const layers = useMemo(() => {
    if (
      !activeEvents ||
      !clusterData ||
      !accidentData ||
      !suggestionData ||
      !interventionCameraData ||
      !govAccidentData
    )
      return [];

    const interventionFeatures = interventionCameraData?.features ?? [];
    const interventionChildLayers = INTERVENTION_CATEGORIES.map(
      ({ id, name, category_id, icon, useSourceIcon = false }) => ({
        id,
        parentId: "icon-camera-layer",
        name,
        visible: true,
        source: {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: interventionFeatures.filter(
              (feature) => Number(feature?.properties?.category_id) === category_id
            ),
          },
        },
        layer: ["IconLayer"],
        layerStyle: {
          useSourceIcon,
          setIcon: icon,
          iconSize: 0.6,
        },
        canHover: true,
        canClick: true,
      })
    );

    return [
      {
        id: "icon-events-layer",
        name: "Συμβάντα",
        visible: true,
        source: {
          type: "geojson",
          data: filteredEvents ?? activeEvents,
        },
        layer: ["IconLayer"],
        layerStyle: {
          useSourceIcon: true,
          iconSize: 0.9,
        },
        canHover: true,
        canClick: true,
      },

      {
        id: "icon-accident-layer",
        name: "Ατυχήματα",
        visible: true,
        source: {
          type: "geojson",
          data: filterAccidents || activeAccidents,
        },
        layer: ["IconLayer"],
        layerStyle: {
          useSourceIcon: false,
          setIcon: accidentIcon,
          iconSize: 0.6,
        },
        canHover: true,
        canClick: true,
      },

      {
        id: "icon-camera-layer",
        isGroup: true,
        children: interventionChildLayers.map((layer) => layer.id),
        name: "Παρεμβάσεις",
        visible: true,
        source: {
          type: "geojson",
          data: interventionCameraData,
        },
        layer: ["IconLayer"],
        layerStyle: {
          useSourceIcon: false,
          setIcon: cameraIcon,
          iconSize: 0.6,
        },
        canHover: true,
        canClick: true,
      },
      ...interventionChildLayers,
      {
        id: "icon-gov-events-layer",
        name: "Ατυχήματα Gov",
        visible: false,
        source: {
          type: "geojson",
          data: govAccidentData,
        },
        layer: ["IconLayer"],
        layerStyle: {
          useSourceIcon: false,
          iconSize: 0.4,
        },
        canHover: true,
        canClick: true,
      },
            {
        id: "point-suggestion-layer",
        name: "Υποδείξεις από φορείς",
        visible: false,
        source: {
          type: "geojson",
          data: suggestionData,
        },
        layer: ["PointLayer"],
        layerStyle: {
          radius: 6,
          color: "#ffffb3",
          opacity: 0.2,
        },
        canHover: true,
        canClick: true,
      },
        {
        id: "point-cluster-layer",
        name: "Cluster",
        visible: false,
        source: {
          type: "geojson",
          data: clusterData,
        },
        layer: ["FillLayer"],
        layerStyle: {
          color: darkMode ? "#cceeff" : "#1565c0",
          clickedOpacity: 0.4,
          hoverOpacity: 0.6,
          defaultOpacity: 0.3,
        },
        canHover: true,
        canClick: false,
      },
    ];
  }, [
    filteredEvents,
    activeEvents,
    filterAccidents,
    accidentData,
    suggestionData,
    interventionCameraData,
    govAccidentData,
    clusterData,
    darkMode,
  ]);

  if (!layers.length) return null;

  const mapUIConfig = {
    id: "layers-map",
    displayControlPanel: !isTablet,
    displayLayerList: !isTablet,
    display3DToggle: true,
    displayFilterPanel: true,
    displayLegend: true,
    displayOpacityControl: true,
    displayZoomControl: true,
    defaultMapStyle: "2d",
    map: {
      center,
      zoom,
      pitch: 0,
      bearing: 0,
      mapToken: config.MAPBOX_ACCESS_TOKEN_3D,
      style: {
        map2D: darkMode
          ? "mapbox://styles/mapbox/dark-v11"
          : "mapbox://styles/mapbox/streets-v12",
        map3D: config.MAP_3D,
      },
    },
  };

  const isCustomPeriod = selectedPeriod === "custom";

  const getSelectedRange = () => {
    const now = dayjs();

    if (selectedPeriod === "custom") {
      if (!startDate || !endDate) {
        return null;
      }

      return {
        start: dayjs(startDate).startOf("day"),
        end: dayjs(endDate).endOf("day"),
      };
    }

    const presetStartMap = {
      last1hour: now.subtract(1, "hour"),
      last12hours: now.subtract(12, "hour"),
      last24hours: now.subtract(24, "hour"),
      lastWeek: now.subtract(1, "week"),
      lastMonth: now.subtract(1, "month"),
    };

    return {
      start: presetStartMap[selectedPeriod] || now.subtract(24, "hour"),
      end: now,
    };
  };

  const isFetchDisabled =
    !eventData || (isCustomPeriod && (!startDate || !endDate));

  const handleFilterEvents = () => {
    if (!eventData) return;

    const selectedRange = getSelectedRange();
    if (!selectedRange) return;

    const { start, end } = selectedRange;

    const filteredEventsArr = eventData.features.filter((f) => {
      const eventStart = dayjs(f.properties.valid_from, "DD/MM/YYYY HH:mm");
      const eventEnd = dayjs(f.properties.valid_until, "DD/MM/YYYY HH:mm");
      const afterStart = start ? eventEnd.isAfter(start) : true;
      const beforeEnd = end ? eventStart.isBefore(end) : true;
      return afterStart && beforeEnd;
    });

    let filteredAcc = null;
    if (accidentData?.features) {
      filteredAcc = accidentData.features.filter((f) => {
        const checkDate = dayjs(f.properties.date, "DD/MM/YYYY HH:mm");
        const afterStart = start ? checkDate.isAfter(start) : true;
        const beforeEnd = end ? checkDate.isBefore(end) : true;
        return afterStart && beforeEnd;
      });
    }

      console.log("FILTER DEBUG:", {
      events: {
        before: eventData.features.length,
        after: filteredEventsArr.length,
      },
      accidents: {
        before: accidentData?.features?.length,
        after: filteredAcc?.length,
      },
    });

    setFilteredEvents({ ...eventData, features: filteredEventsArr });

    if (filteredAcc) {
      setFilterAccidents({ ...accidentData, features: filteredAcc });
    }

    if (isTablet) setOpenDateModal(false);
  };

  const DateFilterContent = (
    <>
      {!isTablet && (
        <div style={{ marginBottom: "8px", fontWeight: "bold", color: panelText }}>
          Φίλτρο Χρονικής Περιόδου
        </div>
      )}

      <FormControl
        fullWidth
        size="small"
        sx={{
          mt: 2,
          backgroundColor: panelBackground,
          borderRadius: 1,
          "& .MuiInputBase-root": {
            height: 40,
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            minHeight: "40px !important",
            paddingTop: 0,
            paddingBottom: 0,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ffffff55",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ffffffaa",
          },
        }}
      >
        <InputLabel sx={{ color: panelText }}>Περίοδος</InputLabel>
        <Select
          value={selectedPeriod}
          label="Περίοδος"
          onChange={(event) => setSelectedPeriod(event.target.value)}
          sx={{
            color: panelText,
            ".MuiSvgIcon-root": { color: panelText },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: panelBackground,
                color: panelText,
              },
            },
          }}
        >
          {PERIOD_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isCustomPeriod && (
        <>
      <div style={{ marginTop: "16px", marginBottom: "10px" }}>
        <DatePicker
          label="Από"
          value={startDate}
          onChange={(newValue) => setStartDate(newValue)}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
                  InputLabelProps: { style: { color: panelText } },
                  inputProps: { style: { color: panelText } },
                  sx: {
                    backgroundColor: panelBackground,
                borderRadius: 1,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ffffff55",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ffffffaa",
                },
              },
            },
          }}
        />
      </div>

      <div>
        <DatePicker
          label="Έως"
          value={endDate}
          onChange={(newValue) => setEndDate(newValue)}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
                  InputLabelProps: { style: { color: panelText } },
                  inputProps: { style: { color: panelText } },
                  sx: {
                    backgroundColor: panelBackground,
                borderRadius: 1,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ffffff55",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ffffffaa",
                },
              },
            },
          }}
        />
      </div>
        </>
      )}

      <Button
        variant="contained"
        onClick={handleFilterEvents}
        disabled={isFetchDisabled}
        sx={{
          marginTop: "10px",
          backgroundColor: "#1976d2",
          color: "#fff",
          "&.MuiButton-contained": {
            textTransform: "capitalize !important",
          },
        }}
      >
        Φιλτράρισμα Συμβάντων & Ατυχημάτων
      </Button>
    </>
  );

  return (
    <DashboardLayout>
      <div style={{ position: "relative" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* Desktop: floating filter panel */}
          {!isTablet && (
            <div
              style={{
                position: "absolute",
                top: "20px",
                left: "20px",
                zIndex: 2,
                background: panelBackground,
                color: panelText,
                padding: "12px",
                borderRadius: "8px",
                border: panelBorder,
                boxShadow: panelShadow,
                fontSize: "14px",
                width: "260px",
              }}
            >
              {DateFilterContent}
            </div>
          )}

          {/* Tablet: μόνο κουμπί Filter που ανοίγει modal */}
          {isTablet && (
            <Stack
              direction="column"
              spacing={1.5}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 2,
              }}
            >
              <IconButton
                onClick={() => setOpenDateModal(true)}
                sx={{
                  backgroundColor: panelBackground,
                  "&:hover": { backgroundColor: darkMode ? "#283352" : "#f0f2f5" },
                  color: panelText,
                  border: panelBorder,
                  borderRadius: "8px",
                }}
              >
                <FilterAlt />
              </IconButton>
            </Stack>
          )}

          {/* Tablet Filter Modal */}
          <Dialog
            open={openDateModal}
            onClose={() => setOpenDateModal(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                backgroundColor: panelBackground,
                color: panelText,
                border: panelBorder,
              },
            }}
          >
            <DialogTitle
              sx={{
                backgroundColor: panelBackground,
                color: panelText,
                borderBottom: darkMode
                  ? "1px solid #ffffff33"
                  : "1px solid rgba(0,0,0,0.12)",
                pr: 6,
              }}
            >
              Φίλτρο Χρονικής Περιόδου
              <IconButton
                onClick={() => setOpenDateModal(false)}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: panelText,
                  borderRadius: "8px",
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                backgroundColor: panelBackground,
                color: panelText,
              }}
            >
              {DateFilterContent}
            </DialogContent>
          </Dialog>
        </LocalizationProvider>

        <MapboxMapComponent layers={layers} mapUIConfig={mapUIConfig} />
      </div>
    </DashboardLayout>
  );
}

export default MapPage;
